import React, { useMemo, useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Text,
  VStack,
  Icon,
  Spinner,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Flex,
  Portal,
  useColorMode,
} from "@chakra-ui/react";
import { FaChevronDown } from "react-icons/fa";
import { AiOutlineFile } from "react-icons/ai";
import { IconWrapper } from "../../components/IconWrapper";
import { getThemeClasses } from "../../utils/themeUtils";

type Channel = {
  id: string;
  name: string;
  organizationId: string;
};

export type Project = {
  id: string;
  name: string;
  description?: string;
  channels: Channel[];
  assignedUsers?: string[];
};

interface ProjectsDropdownProps {
  projects: Project[];
  selectedProject: Project | null;
  projectLoading: boolean;
  onProjectSelect: (project: Project) => void;
}

const ProjectsDropdown: React.FC<ProjectsDropdownProps> = ({
  projects,
  selectedProject,
  onProjectSelect,
  projectLoading,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const projectId = event.target.value;
    const selected = projects.find((project) => project.id === projectId);
    if (selected) {
      onProjectSelect(selected);
    }
  };

  const filteredProjects = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter((p) => p.name.toLowerCase().includes(term));
  }, [projects, searchQuery]);

  return (
    <div className="w-full relative">
      {/* ✅ Show Loader while projects are loading */}
      {projectLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          h="40px"
        >
          <Spinner size="xs" color="yellow.500" />
        </Box>
      ) : projects.length > 0 ? (
        <div className="w-full">
          <Menu>
            <MenuButton
              as={Button}
              bg={isDark ? '#0B0B0F' : 'white'}
              className={`w-full rounded-lg px-4 py-2 shadow-sm transition-all duration-200 ${
                isDark
                  ? 'bg-[#0B0B0F] text-white border border-[#2E2F33]'
                  : 'bg-white text-gray-800 border border-[#E5E7EB]'
              }`}
              rightIcon={
                <IconWrapper
                  icon={FaChevronDown}
                  size={14}
                  className={`${theme.textMuted}`}
                />
              }
              _hover={{
                borderColor: '#B59143',
                backgroundColor: isDark ? '#2A3441' : 'white',
              }}
              _active={{
                borderColor: '#B59143',
                backgroundColor: isDark ? '#2A3441' : 'white',
              }}
              _expanded={{
                borderColor: '#B59143',
                backgroundColor: isDark ? '#2A3441' : 'white',
              }}
            >
              <div className={`flex items-center gap-3 ${theme.text}`}>
                <IconWrapper
                  icon={AiOutlineFile}
                  size={18}
                  className={`${theme.textMuted}`}
                />
                <span className={`text-sm ${
                  selectedProject ? theme.text : (isDark ? 'text-[#C3C9D3]' : 'text-gray-500')
                }`}>
                  {selectedProject ? selectedProject.name : "Choose a project"}
                </span>
              </div>
            </MenuButton>
            <Portal>
              <MenuList className={`p-0 w-full min-w-[260px] z-[20] ${isDark ? 'bg-[#14161B] border border-[#2E2F33]' : 'bg-white border border-[#E5E7EB]'}`} zIndex={20}>
                {/* Search bar */}
                <div className={`sticky top-0 z-10 p-2 border-b ${isDark ? 'border-[#2E2F33] bg-[#14161B]' : 'border-gray-200 bg-white'}`}>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search projects..."
                      className={`w-full rounded-md pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] ${
                        isDark
                          ? 'bg-[#0B0B0F] text-white border border-[#2E2F33]'
                          : 'bg-white text-gray-900 border border-gray-300'
                      }`}
                    />
                    <div className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 ${theme.textMuted} text-xs`}>
                      {filteredProjects.length}
                    </div>
                  </div>
                </div> 

                <div className={`max-h-64 overflow-y-auto ${isDark ? 'bg-[#14161B]' : 'bg-white'}`}>
                  {filteredProjects.length > 0 ? (
                    filteredProjects.map((project) => (
                      <MenuItem
                        key={project.id}
                        onClick={() => onProjectSelect(project)}
                        className={`text-sm ${theme.text} ${isDark ? 'hover:bg-[#2A3441] focus:bg-[#2A3441]' : 'hover:bg-[#FFF7E6] focus:bg-[#FFF7E6]'}`}
                      >
                        {project.name}
                      </MenuItem>
                    ))
                  ) : (
                    <div className={`px-3 py-2 text-xs ${theme.textMuted}`}>
                      No projects found
                    </div>
                  )}
                </div>
              </MenuList>
            </Portal>
          </Menu>
        </div>
      ) : (
        <Box
          mt={0}
          textAlign="center"
          p={2}
          borderRadius="md"
          className={`${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-[#FFF7E6] border border-[#E8C16C]'} shadow-sm`}
        >
          <Text fontSize="xs" fontWeight="bold" className={`${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'}`}>
            You do not have any assigned projects.
          </Text>
        </Box>
      )}
    </div>
  );
};

export default ProjectsDropdown;
